import type { DataFlag, SigningPayload } from "../api/types.js";
import { formatMoney } from "../lib/format.js";
import { describeStep, sourceShown } from "./describe.js";
import { billRule, endsRow, firstIsToday, fullDate, ruleShort, shortDate, upTo } from "./schedule-words.js";

/**
 * DCTA-209 F56 (09-scheduled.md row 27n, mock-up `set-up`) — the success sheet of a signed schedule. Nothing moved, so
 * there is no "What changed": the amount, who and from where in the rule's words, the first payment and the end, and
 * "Nothing has left your accounts." Every value is the signed payload's. Never "Sent".
 */
export interface ScheduledSuccess {
  readonly title: string;
  readonly amount: string;
  readonly sum: string;
  readonly kv: readonly { readonly k: string; readonly v: string }[];
  readonly none: string;
}

export const NOTHING_HAS_LEFT = "Nothing has left your accounts.";

export function scheduledSuccess(payload: SigningPayload, flags: readonly DataFlag[] = []): ScheduledSuccess {
  const b = payload.schedule;
  const step = payload.steps[0];
  if (b === undefined || step === undefined) return { title: "Scheduled", amount: "", sum: "", kv: [], none: NOTHING_HAS_LEFT };
  const name = describeStep(step, flags).name;
  const account = sourceShown(step, flags) ?? "";
  const when = b.cadence === "once" ? `on ${shortDate(b.first_on)}` : ruleShort(b);
  const rule = billRule(b);
  const cap = upTo(b);
  const amount = cap !== undefined ? `Up to ${cap}` : step.type === "funds_transfer" || step.type === "bill_payment" ? formatMoney(step.amount.value, step.amount.currency) : "";
  const sum = rule !== undefined ? `${rule} ${when}, to ${name}` : `${when} to ${name}, from ${account}`;
  // The first payment's date as the customer signed it.
  const first = firstIsToday(b, payload.created_at) ? "Today" : fullDate(b.first_on);
  const ends = endsRow(b);
  const kv = b.cadence === "once" ? [{ k: "Goes out", v: first }] : [{ k: "First payment", v: first }, ...(ends === undefined ? [] : [{ k: "Ends", v: ends.v }])];
  return { title: "Scheduled", amount, sum, kv, none: NOTHING_HAS_LEFT };
}
